<template>
  <v-container fluid class="pa-4">
    <!--
      Admin-only issue tracker. Two feeds land here: CopilotClone's automatic
      tool-failure reporter (deduped; occurrence/regression counters) and the
      agent's own report_issue tool. Same data is manageable through the
      MultiSaaSManager MCP (list_issues / get_issue / update_issue).
    -->
    <v-row align="center" class="mb-2">
      <v-col cols="auto">
        <h1 class="text-h5">Agent Issues</h1>
      </v-col>
      <v-col cols="auto">
        <v-select
          v-model="filters.status"
          :items="statusFilterItems"
          label="Status"
          dense
          hide-details
          clearable
          style="max-width: 170px"
          @change="load"
        />
      </v-col>
      <v-col cols="auto">
        <v-select
          v-model="filters.source"
          :items="sourceItems"
          label="Source"
          dense
          hide-details
          clearable
          style="max-width: 190px"
          @change="load"
        />
      </v-col>
      <v-col cols="auto">
        <v-select
          v-model="filters.environment"
          :items="['local', 'live']"
          label="Environment"
          dense
          hide-details
          clearable
          style="max-width: 150px"
          @change="load"
        />
      </v-col>
      <v-col cols="auto">
        <v-checkbox
          v-model="filters.includeClosed"
          label="Include done/ignored"
          dense
          hide-details
          @change="load"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn small outlined color="primary" :loading="loading" @click="load">
          <v-icon left small>mdi-refresh</v-icon>Refresh
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="issues"
      :loading="loading"
      item-key="id"
      dense
      :items-per-page="50"
      class="elevation-1 issues-table"
      @click:row="openIssue"
    >
      <template v-slot:[`item.severity`]="{ item }">
        <v-chip x-small :color="severityColor(item.severity)" dark>{{
          item.severity
        }}</v-chip>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <v-chip x-small outlined :color="statusColor(item.status)">{{
          item.status
        }}</v-chip>
      </template>
      <template v-slot:[`item.source`]="{ item }">
        <v-icon small :title="item.source">{{
          item.source === "agent_reported"
            ? "mdi-robot"
            : "mdi-alert-circle-outline"
        }}</v-icon>
      </template>
      <template v-slot:[`item.occurrence_count`]="{ item }">
        {{ item.occurrence_count
        }}<span
          v-if="item.regression_count > 0"
          class="error--text font-weight-bold"
          title="Recurred after being marked done"
        >
          (+{{ item.regression_count }} regr.)</span
        >
      </template>
      <template v-slot:[`item.last_seen_at`]="{ item }">
        {{ formatDate(item.last_seen_at) }}
      </template>
    </v-data-table>

    <!-- Detail dialog -->
    <v-dialog v-model="dialog" max-width="900px" scrollable>
      <v-card v-if="selected">
        <v-card-title class="d-flex align-center">
          <span class="text-subtitle-1 font-weight-bold mr-2"
            >#{{ selected.id }}</span
          >
          <v-chip
            x-small
            :color="severityColor(selected.severity)"
            dark
            class="mr-2"
            >{{ selected.severity }}</v-chip
          >
          <v-chip
            x-small
            outlined
            :color="statusColor(selected.status)"
            class="mr-2"
            >{{ selected.status }}</v-chip
          >
          <v-chip x-small outlined class="mr-2">{{
            selected.environment
          }}</v-chip>
          <v-spacer />
          <v-btn
            icon
            small
            color="error"
            title="Delete issue (manual only, keep-forever policy)"
            @click="removeIssue"
          >
            <v-icon small>mdi-delete-outline</v-icon>
          </v-btn>
          <v-btn icon small class="ml-1" title="Close" @click="dialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="pt-2">{{ selected.title }}</v-card-subtitle>
        <v-card-text>
          <div class="mb-2 text-caption">
            <strong>Source:</strong> {{ selected.source }}
            <span v-if="selected.tool_name">
              · <strong>Tool:</strong> {{ selected.tool_name }}</span
            >
            <span v-if="selected.thread_id">
              · <strong>Thread:</strong> {{ selected.thread_id }}
              <em>(inspect via copilot-admin MCP get_chat_transcript)</em></span
            >
            <span v-if="selected.job_id">
              · <strong>Job:</strong> {{ selected.job_id }}</span
            >
            <br />
            <strong>Occurrences:</strong> {{ selected.occurrence_count }}
            <span v-if="selected.regression_count > 0" class="error--text">
              · regressions: {{ selected.regression_count }}</span
            >
            · <strong>First:</strong> {{ formatDate(selected.created_at) }} ·
            <strong>Last:</strong> {{ formatDate(selected.last_seen_at) }}
          </div>

          <div v-if="selected.description" class="mb-3">
            <div class="text-caption font-weight-bold mb-1">Description</div>
            <pre class="issue-pre">{{ selected.description }}</pre>
          </div>

          <div v-if="selected.details_json" class="mb-3">
            <div class="text-caption font-weight-bold mb-1">
              Details (tool args + HTTP)
            </div>
            <pre class="issue-pre">{{ prettyJson(selected.details_json) }}</pre>
          </div>

          <div v-if="selected.admin_notes" class="mb-3">
            <div class="text-caption font-weight-bold mb-1">Notes</div>
            <pre class="issue-pre">{{ selected.admin_notes }}</pre>
          </div>

          <v-row dense align="center" class="mt-2">
            <v-col cols="4">
              <v-select
                v-model="statusEdit"
                :items="statuses"
                label="Set status"
                dense
                hide-details
                @change="setStatus"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="noteText"
                label="Add note"
                dense
                hide-details
                @keyup.enter="addNote"
              />
            </v-col>
            <v-col cols="2">
              <v-btn
                small
                outlined
                color="primary"
                :disabled="!noteText"
                @click="addNote"
                >Add</v-btn
              >
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@shared/services/api";

export default {
  name: "AdminIssuesView",
  data() {
    return {
      loading: false,
      issues: [],
      dialog: false,
      selected: null,
      statusEdit: null,
      noteText: "",
      statuses: [
        "open",
        "in_progress",
        "testing",
        "code_review",
        "done",
        "ignored"
      ],
      sourceItems: [
        { text: "Auto (tool failure)", value: "auto_tool_failure" },
        { text: "Agent reported", value: "agent_reported" }
      ],
      filters: {
        status: null,
        source: null,
        environment: null,
        includeClosed: false
      },
      headers: [
        { text: "#", value: "id", width: 70 },
        { text: "", value: "source", width: 40, sortable: false },
        { text: "Sev", value: "severity", width: 90 },
        { text: "Status", value: "status", width: 120 },
        { text: "Title", value: "title" },
        { text: "Tool", value: "tool_name", width: 170 },
        { text: "Env", value: "environment", width: 80 },
        { text: "Occ", value: "occurrence_count", width: 110 },
        { text: "Last seen", value: "last_seen_at", width: 160 }
      ]
    };
  },
  computed: {
    statusFilterItems() {
      return this.statuses;
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.source) params.source = this.filters.source;
        if (this.filters.environment)
          params.environment = this.filters.environment;
        if (this.filters.includeClosed) params.include_closed = true;
        const response = await api.listIssues(params);
        this.issues = (response.data && response.data.issues) || [];
      } catch (error) {
        console.error("Failed to load issues:", error);
      } finally {
        this.loading = false;
      }
    },
    async openIssue(row) {
      try {
        const response = await api.getIssue(row.id);
        this.selected = response.data;
        this.statusEdit = this.selected.status;
        this.noteText = "";
        this.dialog = true;
      } catch (error) {
        console.error("Failed to load issue:", error);
      }
    },
    async setStatus(newStatus) {
      if (!this.selected || !newStatus || newStatus === this.selected.status)
        return;
      try {
        const response = await api.updateIssue(this.selected.id, {
          status: newStatus
        });
        this.selected = response.data;
        this.load();
      } catch (error) {
        console.error("Failed to update status:", error);
      }
    },
    async addNote() {
      if (!this.selected || !this.noteText) return;
      try {
        const response = await api.updateIssue(this.selected.id, {
          append_note: this.noteText
        });
        this.selected = response.data;
        this.noteText = "";
      } catch (error) {
        console.error("Failed to add note:", error);
      }
    },
    async removeIssue() {
      if (!this.selected) return;
      // Keep-forever policy: deletion is deliberately manual and confirmed.
      if (!window.confirm(`Delete issue #${this.selected.id} permanently?`))
        return;
      try {
        await api.deleteIssue(this.selected.id);
        this.dialog = false;
        this.selected = null;
        this.load();
      } catch (error) {
        console.error("Failed to delete issue:", error);
      }
    },
    severityColor(severity) {
      if (severity === "critical") return "red darken-3";
      if (severity === "high") return "deep-orange";
      if (severity === "medium") return "amber darken-2";
      return "blue-grey";
    },
    statusColor(status) {
      if (status === "open") return "error";
      if (status === "in_progress") return "primary";
      if (status === "testing" || status === "code_review")
        return "amber darken-2";
      if (status === "done") return "success";
      return "grey";
    },
    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
    prettyJson(value) {
      try {
        return JSON.stringify(value, null, 2);
      } catch (error) {
        return String(value);
      }
    }
  }
};
</script>

<style scoped>
.issues-table >>> tbody tr {
  cursor: pointer;
}
.issue-pre {
  background: rgba(127, 127, 127, 0.08);
  border-radius: 4px;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  max-height: 320px;
  overflow: auto;
}
</style>
